import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@clerk/react";
import { Building2, Plus, Pencil, Link2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { makeApi } from "@/lib/api";
import { BrokerDialog, brokerKey, brokerSummary } from "./BrokerDialog";

// value: { broker_id, broker_name }; aiDetails: extra broker details read from a ratecon
export const BrokerPicker = ({ id, brokerId, name, onChange, aiDetails, testId = "broker" }) => {
  const { getToken } = useAuth();
  const api = useMemo(() => makeApi(getToken), [getToken]);
  const [brokers, setBrokers] = useState([]);
  const [open, setOpen] = useState(false);
  const [dialog, setDialog] = useState(null);
  useEffect(() => { api.get("/brokers").then(({ data }) => setBrokers(data)).catch(() => {}); }, [api]);

  const linked = brokers.find((b) => b.id === brokerId);
  const key = brokerKey(name);
  const exact = !brokerId && key ? brokers.find((b) => brokerKey(b.name) === key) : null;
  useEffect(() => { if (exact) onChange({ broker_id: exact.id, broker_name: exact.name }); }, [exact?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (brokerId) setOpen(false); }, [brokerId]);
  const q = (name || "").toLowerCase().trim();
  const matches = brokers.filter((b) => !q || b.name.toLowerCase().includes(q) || brokerKey(b.name).includes(key)).slice(0, 6);
  const pick = (b) => { onChange({ broker_id: b.id, broker_name: b.name }); setOpen(false); };
  const saved = (b) => { setBrokers((bs) => [...bs.filter((x) => x.id !== b.id), b].sort((a, c) => a.name.localeCompare(c.name))); pick(b); };

  return (
    <div className="relative" data-testid={`${testId}-picker`}>
      <Input
        id={id}
        value={name}
        onChange={(e) => { onChange({ broker_id: null, broker_name: e.target.value }); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="Search saved brokers or type a new one"
        autoComplete="off"
        className="mt-1.5"
        data-testid={`${testId}-input`}
      />
      {open && (matches.length > 0 || (q && !linked)) && (
        <div className="absolute z-30 mt-1 w-full rounded-lg border border-slate-200 bg-white shadow-lg overflow-hidden" data-testid={`${testId}-options`}>
          {matches.map((b) => (
            <button key={b.id} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => pick(b)} className="w-full text-left px-3 py-2 hover:bg-slate-50" data-testid={`${testId}-option-${b.id}`}>
              <div className="text-sm font-medium">{b.name}</div>
              {brokerSummary(b) && <div className="text-xs text-slate-500 truncate">{brokerSummary(b)}</div>}
            </button>
          ))}
          {q && !linked && !exact && (
            <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => { setOpen(false); setDialog({ name, ...(aiDetails || {}) }); }} className="w-full text-left px-3 py-2 border-t border-slate-100 text-sm font-medium text-[#002FA7] hover:bg-blue-50" data-testid={`${testId}-add-new`}>
              <Plus className="w-4 h-4 inline mr-1" /> Save &quot;{name}&quot; as a new broker
            </button>
          )}
        </div>
      )}
      {linked ? (
        <div className="mt-1.5 flex items-start gap-1.5 text-xs text-slate-600" data-testid={`${testId}-linked`}>
          <Link2 className="w-3.5 h-3.5 mt-0.5 text-emerald-600 shrink-0" />
          <span className="min-w-0 flex-1 truncate">Saved broker{brokerSummary(linked) ? ` · ${brokerSummary(linked)}` : " · no billing address yet"}</span>
          <button type="button" onClick={() => setDialog(linked)} className="text-[#002FA7] font-medium inline-flex items-center gap-0.5" data-testid={`${testId}-edit`}><Pencil className="w-3 h-3" /> Edit</button>
        </div>
      ) : q ? (
        <div className="mt-1.5 flex items-center gap-1.5 text-xs text-amber-800" data-testid={`${testId}-new-hint`}>
          <Building2 className="w-3.5 h-3.5" /> New broker, saved when you create the load.
          <button type="button" onClick={() => setDialog({ name, ...(aiDetails || {}) })} className="font-medium underline" data-testid={`${testId}-add-details`}>Add details{aiDetails && Object.keys(aiDetails).length ? " (from ratecon)" : ""}</button>
        </div>
      ) : null}
      <BrokerDialog broker={dialog} api={api} onClose={() => setDialog(null)} onSaved={saved} />
    </div>
  );
};
